import { useEffect, useMemo, useState } from 'react';
import type { LocationCaseRecord } from '../../../Api/Locations/locationsApi';
import { useLocationDetail } from '../../../Api/Locations/useLocationDetail';
import { getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { Permission } from '../../../store/GeneralStore';
import { hasFeatureAccess, isCapabilityDenied } from '../../../store/User';
import { accountScopeKey, sameAccount } from '../../../utils/accountIds';
import { ServiceRequestCapability } from '../../../utils/serviceRequestCapabilities';
import { ServiceRequestLink } from '../../Compounds/ServiceRequest/ServiceRequestLink';
import { AddInventoryLink } from '../../Compounds/AddInventoryLink/AddInventoryLink';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { DetailRow } from '../../Compounds/DetailsDrawer/DetailRow';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { RecordUnavailable } from '../../Compounds/RecordUnavailable/RecordUnavailable';
import { LocationCasesSection } from './LocationCasesSection';
import { LocationInventoryTable } from './LocationInventoryTable';
import { LocationOrdersTable } from './LocationOrdersTable';
import { LocationProjectsTable } from './LocationProjectsTable';
import type { LocationGridRow } from './locationsGridColumnDefs';
import { outsideInventoryFromLocation } from './outsideInventoryFromLocation';

interface CreatedCase {
  record: LocationCaseRecord;
  accountId: string;
  scopeKey: string;
}

export interface LocationDetailsProps {
  location: LocationGridRow;
  close: () => void;
}

const canOrderFromLocation = () =>
  !isCapabilityDenied(ServiceRequestCapability.CREATE) && hasFeatureAccess('inventory', undefined, true);

export function LocationDetails({ location, close }: LocationDetailsProps) {
  const { state, retry, scope, accountIds } = useLocationDetail(location.location_key);
  const [projectsEnabled, setProjectsEnabled] = useState<boolean>(getFeatureFlagSubject().getValue().projects === true);
  const [createdCases, setCreatedCases] = useState<CreatedCase[]>([]);

  useEffect(() => {
    const flagsSub = getFeatureFlagSubject().subscribe((flags) => setProjectsEnabled(flags.projects === true));
    return () => flagsSub.unsubscribe();
  }, []);

  const detail = state.status === 'loaded' ? state.detail : undefined;
  const orderLocation = useMemo(
    () =>
      detail && {
        key: location.location_key,
        accountId: location.account_id,
        inventory: detail.inventory.records,
        installed: detail.inventory.records.filter((record) => record.location_roles.includes('install'))
      },
    [detail, location.location_key, location.account_id]
  );
  const scopeKey = accountScopeKey(accountIds);
  const createdInScope = createdCases
    .filter((created) => created.scopeKey === scopeKey || accountIds.some((id) => sameAccount(id, created.accountId)))
    .map((created) => created.record);

  return (
    <div className="mt-6 space-y-4">
      <DetailViewCard
        title="Details"
        cardActions={
          state.status !== 'not_found' && (
            <AddInventoryLink
              state={{ outsideInventory: outsideInventoryFromLocation(location) }}
              dataCy="add-inventory-from-location"
            />
          )
        }>
        <div className="px-3 pt-3">
          <DetailRow
            title="Location Code"
            value={location.location_code ?? ''}
            colId="location_code"
          />
          <DetailRow
            title="Location Name"
            value={location.location_name ?? ''}
            colId="location_name"
          />
          <DetailRow
            title="Customer Location Name"
            value={location.customer_location_name ?? ''}
            colId="customer_location_name"
          />
          <DetailRow
            title="Address"
            value={location.address}
            colId="address"
          />
          <DetailRow
            title="Location Type"
            value={location.location_type}
            colId="location_type"
          />
        </div>
      </DetailViewCard>
      {state.status === 'not_found' && (
        <RecordUnavailable
          message="This location is no longer available."
          actionText="Close"
          onAction={close}
          dataTestId="location-unavailable"
        />
      )}
      {state.status === 'failed' && (
        <RecordUnavailable
          message="The records at this location could not be loaded."
          actionText="Try again"
          onAction={retry}
          dataTestId="location-records-unavailable"
        />
      )}
      {(state.status === 'loading' || state.status === 'loaded') && (
        <LocationInventoryTable
          key={scope}
          inventory={detail?.inventory}
          count={detail?.inventory?.total}
        />
      )}
      {detail?.cases && (
        <PermissibleRender requiredPermissions={[Permission.VIEW_CASES_ON_INVENTORY]}>
          <LocationCasesSection
            cases={detail.cases}
            count={detail.cases.total}
            created={createdInScope}
            onCaseCreated={(case_, account) =>
              setCreatedCases((before) => [
                {
                  record: { ...case_, active: detail.active_case_count === null ? null : true },
                  accountId: account.id,
                  scopeKey
                },
                ...before
              ])
            }
            address={location.address || location.location_name || location.location_code || 'Unnamed location'}
            accountId={location.account_id}
            services={detail.inventory.records}
          />
        </PermissibleRender>
      )}
      {detail?.orders && (
        <LocationOrdersTable
          orders={detail.orders}
          count={detail.orders.total}
          cardActions={
            canOrderFromLocation() && (
              <PermissibleRender requiredPermissions={[Permission.CREATE_SERVICE_REQUESTS]}>
                <ServiceRequestLink
                  ctaClassName="text-grey-12 text-sm"
                  label="New Order"
                  dataCy="new-order-from-location"
                  location={orderLocation}
                />
              </PermissibleRender>
            )
          }
        />
      )}
      {projectsEnabled && detail?.projects && (
        <LocationProjectsTable
          projects={detail.projects}
          count={detail.projects.total}
        />
      )}
    </div>
  );
}
